// CollapsibleSection.js - Reusable collapsible card for organizing dense forms
import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * A collapsible card section used to organize long forms into logical groups.
 *
 * Props:
 * - title:       section heading
 * - icon:        optional lucide icon component
 * - summary:     optional muted text shown next to the title (visible when collapsed)
 * - badge:       optional small badge (string/number) next to the title
 * - defaultOpen: whether the section starts expanded (default false)
 * - className:   extra classes for the outer card
 */
const CollapsibleSection = ({
  id,
  title,
  icon: Icon,
  summary,
  badge,
  defaultOpen = false,
  className = '',
  children
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div id={id} className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors rounded-lg"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {open ? (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          {Icon && <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
          <span className="font-semibold text-gray-800">{title}</span>
          {badge !== undefined && badge !== null && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
              {badge}
            </span>
          )}
          {summary && !open && (
            <span className="text-sm text-gray-400 truncate">{summary}</span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

export default CollapsibleSection;
